import { useTranslation } from 'react-i18next'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatMonthShort, formatNumber } from '@/utils/format'
import type { DashboardData } from '../api'
import { ChartTooltip } from './ChartTooltip'

const BRAND = '#4f46e5'

export function AdmissionsChart({ data }: { data: DashboardData['admissionsByMonth'] }) {
  const { t } = useTranslation('dashboard')
  const points = data.map((d) => ({ ...d, label: formatMonthShort(d.month) }))
  return (
    <figure>
      <div className="h-64" role="img" aria-label={t('New admissions per month over the last 12 months')}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="admissionsFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={BRAND} stopOpacity={0.22} />
                <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke="var(--line)" strokeDasharray="3 4" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'var(--muted)' }}
              dy={6}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'var(--muted)' }}
              width={40}
            />
            <Tooltip
              content={
                <ChartTooltip
                  format={(v) => t('{{value}} admissions', { count: v, value: formatNumber(v) })}
                />
              }
              cursor={{ stroke: 'var(--line-strong)', strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={BRAND}
              strokeWidth={2}
              fill="url(#admissionsFill)"
              activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--surface)' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <details className="mt-2 text-xs text-muted">
        <summary className="cursor-pointer select-none hover:text-ink">{t('View as table')}</summary>
        <table className="mt-2 w-full text-left">
          <thead>
            <tr>
              <th className="py-1 font-medium">{t('Month')}</th>
              <th className="py-1 text-right font-medium">{t('Admissions')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.month} className="border-t border-line">
                <td className="py-1">{d.month}</td>
                <td className="py-1 text-right tabular">{d.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  )
}
